import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { ArrowLeft, ShoppingBag, Sparkles, ExternalLink, MapPin, Heart } from 'lucide-react';

const MERCH_SHOP_URL = 'https://iam-aloha.printify.me/';

const PRODUCTS = [
  {
    slug: 'acp-foam-trucker',
    name: 'ACP Foam Trucker Hat',
    subtitle: 'Aloha Cancer Project · Supportive Awareness Cap',
    price: 12.22,
    badge: 'New',
    image: 'https://images-api.printify.com/mockup/6a24da4c6ff905d8c70c154b/118550/109489/aloha-cancer-project-foam-trucker-hat-supportive-awareness-cap.jpg?camera_label=front&revision=1780800243454',
    url: 'https://iam-aloha.printify.me/product/29134976',
  },
  {
    slug: 'acp-distressed-cap',
    name: 'ACP Distressed Cap',
    subtitle: 'Aloha Cancer Project · Awareness Hat',
    price: 12.22,
    badge: 'New',
    image: 'https://images-api.printify.com/mockup/6a24d9211cdcc2cd9d0df80d/103870/100991/distressed-cap-aloha-cancer-project-awareness-hat.jpg?camera_label=front&revision=1780800135153',
    url: 'https://iam-aloha.printify.me/product/29134946',
  },
  {
    slug: 'acp-floral-tee',
    name: 'ACP Floral Awareness Tee',
    subtitle: 'Aloha Cancer Project · Floral Awareness T-Shirt',
    price: 19.99,
    badge: 'New',
    image: 'https://images-api.printify.com/mockup/6a24d660ac2a0369d80cfc26/38191/97992/aloha-cancer-project-t-shirt-floral-awareness-tee.jpg?camera_label=front&revision=1780800192696',
    url: 'https://iam-aloha.printify.me/product/29134698',
  },
  {
    slug: 'racerback-tank',
    name: "Women's Racerback Tank",
    subtitle: 'Ideal racerback — personalizable',
    price: 19.99,
    badge: 'New',
    image: 'https://images-api.printify.com/mockup/69ffb1fb04b2a2a07e03e37e/19384/111806/womens-ideal-racerback-tank.jpg?camera_label=front-2&revision=1780800044800',
    url: 'https://iam-aloha.printify.me/product/28526923',
  },
  {
    slug: 'festival-tshirt',
    name: 'Festival T-Shirt',
    subtitle: 'Aloha Tequila & Arts Festival — Official tee',
    price: 9.99,
    badge: 'Bestseller',
    image: 'https://images-api.printify.com/mockup/6a23cb5bbeda42506c04a513/38191/127670/aloha-tequila-arts-festival-t-shirt.jpg?camera_label=folded-2&revision=1780730819168',
    url: 'https://iam-aloha.printify.me/product/29118045',
  },
  {
    slug: 'canvas-tote',
    name: 'Aloha Festival Canvas Tote',
    subtitle: 'Tropical tequila & arts market bag',
    price: 17.77,
    badge: 'Festival Fave',
    image: 'https://images-api.printify.com/mockup/6a23ce6038707c982f0f42ed/81758/78958/aloha-festival-canvas-tote-tropical-tequila-arts-market-bag.jpg?camera_label=front&revision=1780731636019',
    url: 'https://iam-aloha.printify.me/product/29118181',
  },
  {
    slug: 'softstyle-tee',
    name: 'Unisex Softstyle Tee',
    subtitle: 'Personalize the front — soft cotton',
    price: 17.77,
    badge: 'Personalize',
    image: 'https://images-api.printify.com/mockup/69d85425a23a63495308d550/38191/97992/unisex-softstyle-t-shirt.jpg?camera_label=front&revision=1780730940686',
    url: 'https://iam-aloha.printify.me/product/28061346',
  },
  {
    slug: 'festival-sticker',
    name: 'Festival Sticker',
    subtitle: 'Square sticker · 6.20.2026 · personalizable',
    price: 2.00,
    badge: 'Under $5',
    image: 'https://images-api.printify.com/mockup/69ffb4dbe2d92d257707bc10/45740/2187/square-sticker-aloha-tequila-arts-festival-6222026.jpg?camera_label=front&revision=1778365804334',
    url: 'https://iam-aloha.printify.me/product/28527090',
  },
];

const fmtPrice = (n) => `$${n.toFixed(2).replace(/\.00$/, '')}`; // eslint-disable-line no-unused-vars -- prices kept in data for future use

const MerchandisePage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a0a14] via-[#0a0a0a] to-black text-white" data-testid="merch-page">
      <Helmet>
        <title>Merch | Aloha Tequila & Arts Festival 2026</title>
        <meta name="description" content="Official Aloha Tequila & Arts Festival merch: hats, T-shirts, tank tops, tote bags, and stickers. Ships worldwide. Proceeds support the Aloha Cancer Project." />
      </Helmet>

      {/* Header */}
      <div className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-gradient-to-br from-pink-500/15 via-orange-500/5 to-amber-500/15 pointer-events-none" />
        <div className="absolute inset-0 opacity-25 pointer-events-none" style={{
          backgroundImage: 'radial-gradient(circle at 25% 30%, rgba(236,72,153,0.18) 0%, transparent 50%), radial-gradient(circle at 75% 70%, rgba(251,191,36,0.15) 0%, transparent 50%)',
        }} />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-10 sm:pt-12 sm:pb-14">
          <Link
            to="/"
            data-testid="merch-back-home"
            className="inline-flex items-center gap-2 text-white/60 hover:text-pink-400 text-sm font-medium mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to homepage
          </Link>

          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-pink-500 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg shadow-pink-500/30">
              <ShoppingBag className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
            </div>
            <div className="inline-flex items-center gap-1.5 bg-pink-500/15 text-pink-300 border border-pink-500/30 rounded-full px-3 py-1 text-xs font-bold">
              <Heart className="w-3 h-3" />
              Proceeds support the Aloha Cancer Project
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight mb-3">
            <span className="bg-gradient-to-r from-pink-400 via-orange-400 to-amber-400 bg-clip-text text-transparent">
              Festival Merch
            </span>
          </h1>
          <p className="text-base sm:text-lg text-white/70 max-w-2xl leading-relaxed">
            Official Aloha Tequila &amp; Arts Festival gear — <strong className="text-white">hats, T-shirts, tank tops, tote bags &amp; stickers</strong>.
            Shop online with worldwide shipping, or pick yours up at the <strong className="text-pink-300">Merch Booth</strong> right when you enter the festival on June 20.
          </p>

          <div className="mt-5 inline-flex items-center gap-1.5 text-xs sm:text-sm text-white/60 bg-white/5 border border-white/10 rounded-full px-3 py-1.5">
            <MapPin className="w-3.5 h-3.5 text-pink-400" />
            Merch Booth — immediately to the left when entering
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {PRODUCTS.map((p) => (
            <a
              key={p.slug}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`merch-card-${p.slug}`}
              className="group block"
            >
              <Card className="relative bg-white/[0.04] backdrop-blur-sm border border-white/10 rounded-3xl overflow-hidden transition-all duration-300 hover:border-pink-500/40 hover:shadow-2xl hover:shadow-pink-500/10 hover:-translate-y-1 cursor-pointer h-full flex flex-col">
                <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-white/5 to-black/30">
                  <img
                    src={p.image}
                    alt={p.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute top-3 right-3 text-[10px] font-black uppercase tracking-wider rounded-full px-2.5 py-1 bg-gradient-to-r from-pink-500 to-orange-500 text-white shadow-lg">
                    {p.badge}
                  </div>
                </div>
                <div className="p-4 sm:p-5 flex-1 flex flex-col">
                  <h3 className="text-white font-bold text-sm sm:text-base leading-tight mb-1">{p.name}</h3>
                  <p className="text-white/55 text-xs mb-4 flex-1">{p.subtitle}</p>
                  <span className="inline-flex items-center gap-1.5 text-pink-400 group-hover:text-pink-300 font-bold text-sm transition-colors">
                    Shop Now <ExternalLink className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </div>
              </Card>
            </a>
          ))}
        </div>

        {/* Online Shop Footer CTA */}
        <div className="mt-10 bg-gradient-to-br from-pink-500/15 via-orange-500/10 to-amber-500/15 border border-pink-500/30 rounded-3xl p-6 sm:p-8 text-center">
          <Sparkles className="w-9 h-9 text-pink-400 mx-auto mb-3" />
          <h3 className="text-xl sm:text-2xl font-black text-white mb-2">Browse the full shop</h3>
          <p className="text-white/70 text-sm sm:text-base mb-5 max-w-xl mx-auto">
            Festival tees, hats, tanks, totes &amp; supplements — ships worldwide via our official Printify shop. New drops all summer.
          </p>
          <a
            href={MERCH_SHOP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="merch-full-shop-link"
          >
            <Button className="bg-gradient-to-r from-pink-500 via-orange-500 to-amber-500 hover:from-pink-600 hover:via-orange-600 hover:to-amber-600 text-white font-black rounded-full px-7 py-2.5 inline-flex items-center gap-2 shadow-xl shadow-pink-500/30">
              Visit the Official Shop
              <ExternalLink className="w-4 h-4" />
            </Button>
          </a>
        </div>

        {/* Footnote */}
        <p className="text-center text-xs text-white/40 mt-8 max-w-2xl mx-auto leading-relaxed">
          Pickup at festival: bring your order confirmation to the Merch Booth.
          Sizes / availability subject to stock. A portion of every sale supports the Aloha Cancer Project.
        </p>
      </div>
    </div>
  );
};

export default MerchandisePage;
